import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useToggle } from 'react-use'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import {
  IconButton,
  type IconButtonProps,
} from '~/components/elements/IconButton'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { SuspenseDialog } from '~/components/elements/SuspenseDialog'
import { useNotify } from '~/hooks/useNotify'

import { useBrandAccountId } from '../hooks/useBrandAccountId'
import { useCheckConflictMutation } from '../hooks/useCheckConflictMutation'
import { useCreateDocumentReportMutation } from '../hooks/useCreateDocumentReportMutation'
import { useInvalidateMainQuery } from '../hooks/useMainQuery'
import { useUpdateDocumentReportMutation } from '../hooks/useUpdateDocumentReportMutation'
import { type InputValues } from '../utils'

import styles from './DocumentReportSaveButton.css'

export const DocumentReportSaveButton = () => {
  const { t } = useTranslation()
  const invalidateMainQuery = useInvalidateMainQuery()
  const notify = useNotify()
  const { getValues, reset } = useFormContext<InputValues>()
  const updateDocumentReportMutation = useUpdateDocumentReportMutation({
    onSuccess: () => {
      reset(getValues())
      invalidateMainQuery()
      notify({ content: t('saved'), style: 'info' })
    },
    onError: () => {
      notify({ content: t('could_not_save_properly'), style: 'error' })
    },
  })

  const [isVisibleConflictDialog, toggleIsVisibleConflictDialog] =
    useToggle(false)
  const checkConflictMutation = useCheckConflictMutation({
    onSuccess: (isConflict) => {
      if (isConflict) {
        toggleIsVisibleConflictDialog()
      } else {
        const values = getValues()
        updateDocumentReportMutation.mutate(values)
      }
    },
  })
  const handleClick = React.useCallback<EH<IconButtonProps, 'onClick'>>(() => {
    checkConflictMutation.mutate()
  }, [checkConflictMutation])

  const handleOverwriteButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    toggleIsVisibleConflictDialog()
    const values = getValues()
    updateDocumentReportMutation.mutate(values)
  }, [getValues, toggleIsVisibleConflictDialog, updateDocumentReportMutation])

  const navigate = useNavigate()
  const brandAccountId = useBrandAccountId()
  const createDocumentReportMutation = useCreateDocumentReportMutation({
    onSuccess: (documentReportId) => {
      reset()
      navigate(
        compile('documentReportEdit', { brandAccountId, documentReportId }),
      )
    },
    onError: () => {
      notify({ content: t('could_not_save_properly'), style: 'error' })
    },
  })
  const handleSaveAsNewButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    toggleIsVisibleConflictDialog()
    const values = getValues()
    createDocumentReportMutation.mutate(values)
  }, [createDocumentReportMutation, getValues, toggleIsVisibleConflictDialog])

  return (
    <>
      <IconButton
        disabled={
          checkConflictMutation.isPending ||
          updateDocumentReportMutation.isPending ||
          createDocumentReportMutation.isPending
        }
        fillMode="outline"
        icon="save"
        onClick={handleClick}
      />

      {isVisibleConflictDialog && (
        <SuspenseDialog
          actions={
            <>
              <StyledButton onClick={handleOverwriteButtonClick}>
                {t('overwrite_and_save')}
              </StyledButton>

              <StyledButton onClick={handleSaveAsNewButtonClick}>
                {t('save_as')}
              </StyledButton>

              <StyledButton
                buttonStyle="light"
                fillMode="outline"
                onClick={toggleIsVisibleConflictDialog}
              >
                {t('dont_save')}
              </StyledButton>
            </>
          }
          className={styles.conflictDialog}
          title={t('confirm')}
          onClose={toggleIsVisibleConflictDialog}
        >
          {t('this_may_overwrite_changes_made_by_others_continue_anyway')}
        </SuspenseDialog>
      )}
    </>
  )
}
